import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../../utils/axios';
import BottomNav from '../../components/layouts/BottomNav';
import { FaArrowLeft } from 'react-icons/fa';
import { useCart } from '../../contexts/CartContext';
import { useToast } from '../../contexts/ToastContext';
import { formatPrice } from '../../utils/priceFormatter';
import { API_BASE } from '../../utils/url';

const ProductsPage = () => {
  const { addToCart, cartCount } = useCart();
  const { showSuccess, showError } = useToast();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({
    category: '',
    color: '',
    shade: '',
    grain: '',
    finish: '',
    substrate: '',
    veneer_thickness: '',
    dimension: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [filterOptions, setFilterOptions] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);
  const [userFrozen, setUserFrozen] = useState({}); // productId -> { total, latest, product }
  const [editFreeze, setEditFreeze] = useState({ isOpen: false, latest: null, total: 0, newQty: 1 });

  // Freeze quantity popup state
  const [freezePopup, setFreezePopup] = useState({
    isOpen: false,
    product: null,
    quantity: 1,
    client_name: '',
    architect_name: '',
    remarks: ''
  });

  // Populate filters from URL on first load
  useEffect(() => {
    setFilters({
      category: searchParams.get('category') || '',
      color: searchParams.get('color') || '',
      shade: searchParams.get('shade') || '',
      grain: searchParams.get('grain') || '',
      grain_direction: searchParams.get('grain_direction') || '',
      finish: searchParams.get('finish') || '',
      substrate: searchParams.get('substrate') || '',
      veneer_thickness: searchParams.get('veneer_thickness') || '',
      dimension: searchParams.get('dimension') || ''
    });
    setSearchTerm(searchParams.get('search') || '');
  }, [searchParams]);

  // Fetch all products once for filter options
  useEffect(() => {
    api.get('/products').then(res => {
      // Generate unique filter options from ALL products
      const opts = {
        color: [],
        shade: [],
        grain: [],
        grain_direction: [],
        finish: [],
        substrate: [],
        veneer_thickness: [],
        dimension: []
      };
      res.data.forEach(prod => {
        Object.keys(opts).forEach(key => {
          if (prod[key] && !opts[key].includes(prod[key])) opts[key].push(prod[key]);
        });
      });
      setFilterOptions(opts);
    });
  }, []);

  // Fetch categories and filtered products when filters/search change
  useEffect(() => {
    let isMounted = true; // Flag to track if the effect is still active
    setIsLoading(true);

    // Fetch categories mostly for the sidebar, harmless if race condition happens here but good to be safe
    api.get('/categories')
      .then(res => {
        if (isMounted) setCategories(res.data);
      })
      .catch(console.error);

    // Build API params
    let params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    if (searchTerm) params.search = searchTerm;

    console.log('Fetching products with params:', params);

    api.get('/products', { params })
      .then(res => {
        if (isMounted) {
          console.log('Products received:', res.data.length);
          setProducts(res.data);
          setIsLoading(false);
        }
      })
      .catch(err => {
        if (isMounted) {
          console.error('Error fetching products:', err);
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false; // Cleanup: ignore results from this effect run
    };
  }, [filters, searchTerm]);

  // Fetch user's active frozen quantities (for showing Edit)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return; // not logged in
    api.get('/frozen-quantities/user').then(res => {
      const map = {};
      (res.data || []).forEach(fq => {
        if (!fq?.product?.id) return;
        const pid = fq.product.id;
        if (!map[pid]) map[pid] = { total: 0, latest: fq, product: fq.product, records: [] };
        map[pid].total += Number(fq.quantity || 0);
        map[pid].records.push(fq);
        // pick latest by frozen_at
        if (new Date(fq.frozen_at) > new Date(map[pid].latest.frozen_at)) map[pid].latest = fq;
      });
      setUserFrozen(map);
    }).catch(() => { });
  }, []);

  // Handlers
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const newFilters = { ...filters, [name]: value };
    setFilters(newFilters);

    // Update URL params
    const params = {};
    Object.entries(newFilters).forEach(([k, v]) => { if (v) params[k] = v; });
    if (searchTerm) params.search = searchTerm;
    setSearchParams(params);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    if (searchTerm) params.search = searchTerm;
    setSearchParams(params);
  };

  const handleProductClick = (prodId) => {
    navigate(`/products/${prodId}`);
  };

  // Handle freeze quantity
  const handleFreezeQuantity = (product) => {
    setFreezePopup({
      isOpen: true,
      product: product,
      quantity: 1,
      client_name: '',
      architect_name: '',
      remarks: ''
    });
  };

  // Close freeze popup
  const closeFreezePopup = () => {
    setFreezePopup({
      isOpen: false,
      product: null,
      quantity: 1,
      client_name: '',
      architect_name: '',
      remarks: ''
    });
  };

  // Confirm freeze quantity
  const confirmFreezeQuantity = async () => {
    const { product, quantity, client_name, architect_name, remarks } = freezePopup;

    if (quantity <= 0) {
      showError('Quantity must be greater than 0');
      return;
    }

    const availableQty = product.available_quantity || product.inventory;
    if (quantity > availableQty) {
      showError(`Only ${availableQty} units available for ${product.name}`);
      return;
    }

    // Validate mandatory fields
    if (!client_name || !client_name.trim()) {
      showError('Client Name is required');
      return;
    }

    if (!architect_name || !architect_name.trim()) {
      showError('Architect Name is required');
      return;
    }

    if (!remarks || !remarks.trim()) {
      showError('Remarks is required');
      return;
    }

    try {
      const response = await api.post('/frozen-quantities/freeze', {
        product_id: product.id,
        quantity: quantity,
        client_name: client_name,
        architect_name: architect_name,
        remarks: remarks
      });

      showSuccess(response.data.message);
      closeFreezePopup();

      // Refresh products to update frozen quantities
      window.location.reload();
    } catch (error) {
      console.error('Error freezing quantity:', error);
      showError(error.response?.data?.message || 'Failed to freeze quantity');
    }
  };

  // Search bar
  const searchBar = (
    <form onSubmit={handleSearch} style={{ marginBottom: 18, marginTop: 18 }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        background: '#fff',
        borderRadius: 12,
        border: '2px solid #353535',
        boxShadow: "0 1px 8px 0 rgba(0,0,0,0.03)",
        padding: '5px 12px'
      }}>
        <svg width="20" height="20" fill="none" stroke="#888" strokeWidth="2" style={{ marginRight: 8 }} viewBox="0 0 24 24">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          type="text"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search by product name or product ID"
          style={{
            border: "none",
            outline: "none",
            background: "transparent",
            fontSize: 15,
            color: "#222",
            flex: 1,
            padding: 5
          }}
        />
        <button type="submit"
          style={{
            background: "#353535",
            color: "#fff",
            border: "none",
            borderRadius: 7,
            padding: "6px 16px",
            fontWeight: 600,
            fontSize: 15,
            marginLeft: 6,
            cursor: "pointer"
          }}>
          Search
        </button>
      </div>
    </form>
  );

  // Modern dropdown (for filters except category)
  const dropdown = (name, options, label) => (
    <select
      name={name}
      value={filters[name]}
      onChange={handleFilterChange}
      className="products-filter-select"
      style={{
        border: '1px solid #e0e0e0',
        background: "#ffffff",
        borderRadius: "8px",
        padding: "12px 16px",
        fontSize: 14,
        color: "#333333",
        width: "100%",
        minWidth: "auto",
        transition: "all 0.2s ease",
        appearance: "none",
        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23666' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "right 12px center",
        backgroundSize: "16px",
        paddingRight: "40px"
      }}
      onFocus={(e) => {
        e.target.style.borderColor = "#000000";
        e.target.style.boxShadow = "0 0 0 2px rgba(0,0,0,0.1)";
      }}
      onBlur={(e) => {
        e.target.style.borderColor = "#e0e0e0";
        e.target.style.boxShadow = "none";
      }}
      onMouseEnter={(e) => {
        e.target.style.borderColor = "#cccccc";
      }}
      onMouseLeave={(e) => {
        if (document.activeElement !== e.target) {
          e.target.style.borderColor = "#e0e0e0";
        }
      }}
    >
      <option value="">{label}</option>
      {options.map((val, idx) => (
        <option key={idx} value={val}>{val}</option>
      ))}
    </select>
  );

  // Get category name(s) for display (support multi-category)
  const getCategoryNames = (product) => {
    if (product.categories && product.categories.length > 0) {
      return product.categories.map(c => c.name).join(', ');
    }
    // Fallback for single category id:
    if (product.category && categories.length > 0) {
      const cat = categories.find(c => String(c.id) === String(product.category));
      return cat ? cat.name : '';
    }
    return '';
  };

  // Robust image parser (for any format: array, JSON, comma string)
  const getFirstImageUrl = (product) => {
    let imageUrl = "";
    if (Array.isArray(product.images)) {
      imageUrl = product.images[0];
    } else if (typeof product.images === "string" && product.images.trim() !== "") {
      try {
        const parsed = JSON.parse(product.images);
        if (Array.isArray(parsed) && parsed.length > 0) {
          imageUrl = parsed[0];
        } else {
          imageUrl = product.images.split(",")[0].trim();
        }
      } catch {
        imageUrl = product.images.split(",")[0].trim();
      }
    }
    if (imageUrl && !imageUrl.startsWith("http")) {
      imageUrl = API_BASE + imageUrl.replace(/^\/+/, "");
    }
    return imageUrl;
  };

  return (
    <div style={{ background: "#fff", minHeight: "100vh", paddingBottom: 74 }}>
      {/* Header & Filters */}
      <div style={{
        padding: "18px 10px 0 10px",
        background: "#fff",
        position: "sticky",
        top: 0,
        zIndex: 9
      }}>
        {/* Row: Back Button + Page Title + Cart */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "0 6px 10px 6px", marginBottom: 8
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
            {/* Back Button */}
            <button
              onClick={() => navigate('/dashboard')}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                marginRight: 10,
                display: "flex",
                alignItems: "center",
                cursor: "pointer"
              }}
              aria-label="Back"
            >
              <svg width="25" height="25" viewBox="0 0 24 24" fill="none"
                stroke="#222" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <div style={{
              fontWeight: 700,
              fontSize: 21,
              color: "#232323",
              letterSpacing: "0.2px",
              marginTop: 2
            }}>
              Veneer Gallery
            </div>
          </div>

          {/* Cart Icon with Count */}
          <div
            onClick={() => navigate('/cart')}
            style={{
              position: "relative",
              cursor: "pointer"
            }}
          >
            <div style={{
              width: 40,
              height: 40,
              background: "#f5f5f5",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 18,
              color: "#333",
              fontWeight: "bold",
              border: "1px solid #e0e0e0"
            }}>
              🛒
            </div>
            {cartCount > 0 && (
              <div style={{
                position: "absolute",
                top: -5,
                right: -5,
                background: "#ef4444",
                color: "#fff",
                borderRadius: "50%",
                width: 20,
                height: 20,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                fontWeight: "bold"
              }}>
                {cartCount}
              </div>
            )}
          </div>
        </div>

        {searchBar}

        {/* Filter Button */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 14, color: "#666" }}>
            {products.length} products found
          </div>
          <button
            onClick={() => setIsFilterSidebarOpen(true)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              background: "#f5f5f5",
              border: "1px solid #ddd",
              borderRadius: 8,
              padding: "8px 12px",
              fontSize: 14,
              color: "#333",
              cursor: "pointer",
              fontWeight: 500
            }}
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46" />
            </svg>
            Filters
          </button>
        </div>
      </div>

      {/* Filter Sidebar */}
      {isFilterSidebarOpen && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => setIsFilterSidebarOpen(false)}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.5)",
              zIndex: 1000,
              animation: "fadeIn 0.3s ease-out"
            }}
          />

          {/* Mobile App Style Sidebar */}
          <div style={{
            position: "fixed",
            top: 0,
            right: 0,
            width: "100%",
            height: "100vh",
            background: "#ffffff",
            zIndex: 1001,
            animation: "slideInRight 0.3s ease-out",
            overflowY: "auto",
            borderTopLeftRadius: "20px",
            borderBottomLeftRadius: "20px"
          }}>
            {/* Mobile App Header */}
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "16px 20px",
              background: "#ffffff",
              position: "sticky",
              top: 0,
              zIndex: 2,
              borderBottom: "1px solid #f0f0f0",
              borderTopLeftRadius: "20px"
            }}>
              <div style={{
                fontSize: 18,
                fontWeight: 700,
                color: "#000000",
                letterSpacing: "-0.3px"
              }}>
                Filters
              </div>
              <button
                onClick={() => setIsFilterSidebarOpen(false)}
                style={{
                  background: "#f5f5f5",
                  border: "none",
                  fontSize: 20,
                  color: "#666",
                  cursor: "pointer",
                  padding: "8px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "36px",
                  height: "36px",
                  transition: "all 0.2s ease"
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "#e8e8e8";
                  e.target.style.color = "#333";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "#f5f5f5";
                  e.target.style.color = "#666";
                }}
              >
                ×
              </button>
            </div>

            {/* Mobile App Content */}
            <div style={{ padding: "20px" }}>
              <div className="products-filters-container" style={{ display: "flex", flexDirection: "column", gap: 20 }}>

                {/* Category Section */}
                <div style={{ marginBottom: "20px" }}>
                  <div style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#000000",
                    marginBottom: 12,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px"
                  }}>
                    <div style={{
                      width: "3px",
                      height: "12px",
                      background: "#007bff",
                      borderRadius: "2px"
                    }}></div>
                    Category
                  </div>
                  <select
                    name="category"
                    value={filters.category}
                    onChange={handleFilterChange}
                    className="products-filter-select"
                    style={{
                      border: '1px solid #ddd',
                      background: "#fff",
                      borderRadius: "8px",
                      padding: "10px 14px",
                      fontSize: 14,
                      color: "#333",
                      width: "100%",
                      transition: "all 0.2s ease",
                      appearance: "none",
                      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23666' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "right 12px center",
                      backgroundSize: "16px",
                      paddingRight: "40px",
                      cursor: "pointer"
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#000";
                      e.target.style.boxShadow = "0 0 0 3px rgba(0,0,0,0.05)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#ddd";
                      e.target.style.boxShadow = "none";
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.borderColor = "#999";
                    }}
                    onMouseLeave={(e) => {
                      if (document.activeElement !== e.target) {
                        e.target.style.borderColor = "#ddd";
                      }
                    }}
                  >
                    <option value="">All Categories</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                {/* Appearance Section */}
                <div style={{ marginBottom: "20px" }}>
                  <div style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#000000",
                    marginBottom: 12,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px"
                  }}>
                    <div style={{
                      width: "3px",
                      height: "12px",
                      background: "#28a745",
                      borderRadius: "2px"
                    }}></div>
                    Appearance
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 8, display: "block", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                        Color
                      </label>
                      {dropdown("color", filterOptions.color || [], "All Colors")}
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 8, display: "block", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                        Shade
                      </label>
                      {dropdown("shade", filterOptions.shade || [], "All Shades")}
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 8, display: "block", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                        Grain
                      </label>
                      {dropdown("grain", filterOptions.grain || [], "All Grains")}
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 8, display: "block", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                        Direction of Grain
                      </label>
                      {dropdown("grain_direction", filterOptions.grain_direction || [], "All Directions")}
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 8, display: "block", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                        Finish
                      </label>
                      {dropdown("finish", filterOptions.finish || [], "All Finishes")}
                    </div>
                  </div>
                </div>

                {/* Specifications Section */}
                <div style={{ marginBottom: "20px" }}>
                  <div style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#000000",
                    marginBottom: 12,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px"
                  }}>
                    <div style={{
                      width: "3px",
                      height: "12px",
                      background: "#ffc107",
                      borderRadius: "2px"
                    }}></div>
                    Specifications
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 8, display: "block", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                        Substrate
                      </label>
                      {dropdown("substrate", filterOptions.substrate || [], "All Substrates")}
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 8, display: "block", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                        Thickness
                      </label>
                      {dropdown("veneer_thickness", filterOptions.veneer_thickness || [], "All Thicknesses")}
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 8, display: "block", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                        Dimension
                      </label>
                      {dropdown("dimension", filterOptions.dimension || [], "All Dimensions")}
                    </div>
                  </div>
                </div>

                {/* Mobile App Action Buttons */}
                <div style={{
                  display: "flex",
                  gap: 10,
                  marginTop: 20,
                  paddingTop: 20,
                  borderTop: "1px solid #e9ecef"
                }}>
                  <button
                    onClick={() => {
                      setFilters({
                        category: '',
                        color: '',
                        shade: '',
                        grain: '',
                        grain_direction: '',
                        finish: '',
                        substrate: '',
                        veneer_thickness: '',
                        dimension: ''
                      });
                      setSearchParams({});
                    }}
                    style={{
                      flex: 1,
                      background: "#f5f5f5",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      padding: "12px 16px",
                      fontSize: 14,
                      color: "#666666",
                      cursor: "pointer",
                      fontWeight: 500,
                      transition: "all 0.2s ease"
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = "#e8e8e8";
                      e.target.style.color = "#333333";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "#f5f5f5";
                      e.target.style.color = "#666666";
                    }}
                  >
                    Clear All
                  </button>
                  <button
                    onClick={() => setIsFilterSidebarOpen(false)}
                    style={{
                      flex: 1,
                      background: "#000000",
                      border: "none",
                      borderRadius: "8px",
                      padding: "12px 16px",
                      fontSize: 14,
                      color: "#ffffff",
                      cursor: "pointer",
                      fontWeight: 600,
                      transition: "all 0.2s ease"
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = "#333333";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "#000000";
                    }}
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Products Grid */}
      {isLoading ? (
        <div style={{ textAlign: "center", padding: 40 }}>Loading...</div>
      ) : (
        <div
          className="products-grid-mobile"
          style={{
            padding: "14px 6px 0 6px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 12
          }}
        >
          {products.length === 0 && (
            <div className="no-products-message" style={{ gridColumn: "span 3", textAlign: "center", color: "#aaa", padding: 34 }}>
              No products found. <br />
              <button onClick={() => {
                setFilters({
                  category: '',
                  color: '',
                  shade: '',
                  grain: '',
                  finish: '',
                  substrate: '',
                  veneer_thickness: '',
                  dimension: ''
                });
                setSearchTerm('');
                setSearchParams({});
              }} style={{
                color: "#111",
                fontWeight: 600,
                background: "#f5f5f5",
                border: "none",
                padding: "8px 18px",
                borderRadius: 6,
                marginTop: 14,
                cursor: "pointer"
              }}>
                Clear Filters
              </button>
            </div>
          )}
          {products.map(product => {
            const imageUrl = getFirstImageUrl(product);
            return (
              <div
                key={product.id}
                style={{
                  background: "#fcfcfd",
                  borderRadius: 12,
                  boxShadow: "0 1px 7px 0 rgba(40,44,63,0.04)",
                  padding: 8,
                  textAlign: "left",
                  cursor: "pointer",
                  transition: "box-shadow 0.16s",
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  justifyContent: "space-between",
                  minWidth: 0
                }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    onClick={() => handleProductClick(product.id)}
                    style={{
                      width: "100%",
                      paddingTop: "133.33%", // 3:4 aspect ratio
                      position: 'relative',
                      borderRadius: 10,
                      overflow: "hidden",
                      background: "#f4f4f4",
                      marginBottom: 6,
                      cursor: "pointer",
                      flexShrink: 0
                    }}>
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={product.name}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block"
                        }}
                      />
                    ) : (
                      <span style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#ccc", fontSize: 10 }}>No Img</span>
                    )}
                  </div>

                  {/* Category Name */}
                  {getCategoryNames(product) && (
                    <div style={{
                      fontWeight: 500,
                      fontSize: 10,
                      color: "#999",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      marginBottom: 2,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    }}>
                      {getCategoryNames(product)}
                    </div>
                  )}

                  {/* Product Name */}
                  <div
                    onClick={() => handleProductClick(product.id)}
                    style={{
                      fontWeight: 600,
                      fontSize: 13,
                      color: "#222",
                      marginBottom: 3,
                      cursor: "pointer",
                      lineHeight: 1.2,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}
                    title={product.name}
                  >
                    {product.name}
                  </div>

                  {/* Price hidden as per user request */}

                  {/* Stock Status */}
                  <div style={{
                    fontSize: 10,
                    color: product.available_quantity > 5 ? "#22c55e" : product.available_quantity > 0 ? "#f59e0b" : "#ef4444",
                    marginBottom: 4,
                    fontWeight: 500
                  }}>
                    {product.available_quantity > 0 ? (
                      <>{product.available_quantity} Avl</>
                    ) : (
                      'Out of Stock'
                    )}
                  </div>
                </div>

                {/* Bottom Section: Tables & Buttons (Pushed to bottom) */}
                <div style={{ marginTop: "auto", width: '100%' }}>
                  {/* Tech Specs Table */}
                  <div style={{ marginTop: 4, marginBottom: 8, fontSize: 9, color: "#666" }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', lineHeight: '1.3' }}>
                      <tbody>
                        {[
                          { k: 'grain', l: 'Grain' },
                          { k: 'grain_direction', l: 'Dir' },
                          { k: 'veneer_thickness', l: 'Thk' }
                        ].map(field => product[field.k] && (
                          <tr key={field.k} style={{ borderTop: '1px solid #d1d1d1' }}>
                            <td style={{ padding: '2px 0', color: '#888' }}>{field.l}:</td>
                            <td style={{ padding: '2px 0', textAlign: 'right', fontWeight: 500, color: '#333' }}>{product[field.k]}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Buttons Container */}
                  <div
                    className="products-buttons-mobile"
                    style={{
                      display: "flex",
                      gap: 2,
                      marginBottom: 0,
                      alignItems: "center"
                    }}
                  >
                    {/* Add Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // Check inventory
                        if (product.available_quantity <= 0) {
                          showError(`${product.name} is out of stock`);
                          return;
                        }
                        addToCart(product, 1);
                        showSuccess(`${product.name} added to cart!`);
                      }}
                      disabled={product.available_quantity <= 0}
                      style={{
                        flex: 1,
                        height: 26,
                        padding: "0 2px",
                        background: "#fff",
                        color: product.available_quantity <= 0 ? "#9aa0a6" : "#222",
                        border: "1px solid #e5e7eb",
                        borderRadius: 4,
                        fontSize: 9,
                        fontWeight: 600,
                        cursor: product.available_quantity <= 0 ? "not-allowed" : "pointer",
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                      }}
                    >
                      {product.available_quantity <= 0 ? 'Out' : 'Add'}
                    </button>

                    {/* Freeze Qty Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFreezeQuantity(product);
                      }}
                      disabled={product.available_quantity <= 0}
                      style={{
                        flex: 1,
                        height: 26,
                        padding: "0 2px",
                        background: "#fff",
                        color: product.available_quantity <= 0 ? "#9aa0a6" : "#222",
                        border: "1px solid #e5e7eb",
                        borderRadius: 4,
                        fontSize: 9,
                        fontWeight: 600,
                        cursor: product.available_quantity <= 0 ? "not-allowed" : "pointer",
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                      }}
                    >
                      Freeze
                    </button>

                    {/* Edit Freeze Button (if user has one) */}
                    {userFrozen[product.id] && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const data = userFrozen[product.id];
                          setEditFreeze({ isOpen: true, latest: data.latest, total: data.total, newQty: data.total });
                        }}
                        style={{
                          width: 26,
                          height: 26,
                          padding: 0,
                          background: '#fff',
                          color: '#222',
                          border: '1px solid #e5e7eb',
                          borderRadius: 4,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          flexShrink: 0
                        }}
                        aria-label="Edit Freeze"
                        title="Edit Freeze"
                      >
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 20h9" />
                          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Freeze Quantity Popup */}
      {freezePopup.isOpen && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
          padding: "20px"
        }}>
          <div style={{
            background: "#fff",
            borderRadius: 12,
            padding: "24px",
            maxWidth: "400px",
            width: "100%",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.2)"
          }}>
            {/* Header */}
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px"
            }}>
              <h3 style={{
                fontSize: "18px",
                fontWeight: "600",
                color: "#333",
                margin: 0
              }}>
                Freeze Quantity
              </h3>
              <button
                onClick={closeFreezePopup}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  color: "#999",
                  cursor: "pointer",
                  padding: 0,
                  width: "24px",
                  height: "24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                ×
              </button>
            </div>

            {/* Product Details - Compact */}
            {freezePopup.product && (
              <div style={{
                background: "#f8f9fa",
                borderRadius: 8,
                padding: "10px",
                marginBottom: "16px",
                display: "flex",
                alignItems: "center",
                gap: "10px"
              }}>
                <img
                  src={getFirstImageUrl(freezePopup.product)}
                  alt={freezePopup.product.name}
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: 6,
                    objectFit: "cover",
                    background: "#eee"
                  }}
                  onError={(e) => { e.target.style.display = "none"; }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: "600", fontSize: "14px", color: "#333", lineHeight: 1.2 }}>
                    {freezePopup.product.name}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                    {/* Price hidden as per user request */}
                    <div style={{ fontSize: "11px", color: freezePopup.product.available_quantity > 0 ? "#22c55e" : "#ef4444", fontWeight: 500 }}>
                      {freezePopup.product.available_quantity} Avl
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Quantity Input - Compact */}
            <div style={{ marginBottom: "16px", display: "flex", flexDirection: "column", alignItems: "center" }}>
              <label style={{ fontSize: "13px", fontWeight: "500", color: "#555", marginBottom: "8px" }}>Quantity to Freeze</label>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <button
                  onClick={() => setFreezePopup(prev => ({ ...prev, quantity: Math.max(1, prev.quantity - 1) }))}
                  style={{
                    width: "36px", height: "36px",
                    background: "#fff", border: "1px solid #ddd", borderRadius: "50%",
                    cursor: "pointer", fontSize: "18px", display: "flex", alignItems: "center", justifyContent: "center",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
                  }}
                >−</button>
                <input
                  type="number"
                  min="1"
                  max={freezePopup.product?.available_quantity || 1}
                  value={freezePopup.quantity}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 1;
                    const maxQty = freezePopup.product?.available_quantity || 1;
                    setFreezePopup(prev => ({ ...prev, quantity: Math.max(1, Math.min(maxQty, value)) }));
                  }}
                  style={{
                    width: "70px", height: "36px", textAlign: "center",
                    fontSize: "16px", fontWeight: "600",
                    border: "1px solid #ddd", borderRadius: 6, outline: "none",
                    background: "#fff"
                  }}
                />
                <button
                  onClick={() => setFreezePopup(prev => ({ ...prev, quantity: Math.min(freezePopup.product?.available_quantity || 1, prev.quantity + 1) }))}
                  style={{
                    width: "36px", height: "36px",
                    background: "#fff", border: "1px solid #ddd", borderRadius: "50%",
                    cursor: "pointer", fontSize: "18px", display: "flex", alignItems: "center", justifyContent: "center",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
                  }}
                >+</button>
              </div>
            </div>

            {/* Form Fields - Compact Grouped */}
            <div style={{ marginBottom: "20px" }}>
              <div style={{ display: "flex", gap: "10px", marginBottom: "12px" }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "500", marginBottom: "4px", color: "#444" }}>Client Name *</label>
                  <input
                    type="text"
                    value={freezePopup.client_name}
                    onChange={(e) => setFreezePopup(prev => ({ ...prev, client_name: e.target.value }))}
                    placeholder="Client Name"
                    required
                    style={{
                      width: "100%", padding: "8px 10px",
                      border: "1px solid #ddd", borderRadius: 6, fontSize: "13px", outline: "none"
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "500", marginBottom: "4px", color: "#444" }}>Architect Name *</label>
                  <input
                    type="text"
                    value={freezePopup.architect_name}
                    onChange={(e) => setFreezePopup(prev => ({ ...prev, architect_name: e.target.value }))}
                    placeholder="Architect Name"
                    required
                    style={{
                      width: "100%", padding: "8px 10px",
                      border: "1px solid #ddd", borderRadius: 6, fontSize: "13px", outline: "none"
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "500", marginBottom: "4px", color: "#444" }}>Remarks *</label>
                <textarea
                  value={freezePopup.remarks}
                  onChange={(e) => setFreezePopup(prev => ({ ...prev, remarks: e.target.value }))}
                  placeholder="Enter remarks"
                  required
                  rows={2}
                  style={{
                    width: "100%", padding: "8px 10px",
                    border: "1px solid #ddd", borderRadius: 6, fontSize: "13px", outline: "none", resize: "none"
                  }}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                onClick={closeFreezePopup}
                style={{
                  flex: 1, padding: "10px",
                  background: "#f3f4f6", color: "#555",
                  border: "1px solid #e5e7eb", borderRadius: 8,
                  fontSize: "14px", fontWeight: "500", cursor: "pointer"
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmFreezeQuantity}
                style={{
                  flex: 1, padding: "10px",
                  background: "#000", color: "#fff",
                  border: "1px solid #000", borderRadius: 8,
                  fontSize: "14px", fontWeight: "600", cursor: "pointer"
                }}
              >
                Freeze
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Freeze Popup */}
      {editFreeze.isOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 20, width: '100%', maxWidth: 360 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontWeight: 600, fontSize: 16 }}>Edit Freeze Quantity</div>
              <button onClick={() => setEditFreeze({ isOpen: false, record: null, newQty: 1 })} style={{ background: 'none', border: 'none', fontSize: 22, color: '#999', cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ fontSize: 13, color: '#555', marginBottom: 6 }}>
              {editFreeze.record?.product?.name || ''}
            </div>
            {editFreeze.latest && (
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                Previously: <strong>{editFreeze.total}</strong> unit{editFreeze.total > 1 ? 's' : ''}
                {editFreeze.latest.expires_at && (
                  <>
                    {' '}• Expires: {new Date(editFreeze.latest.expires_at).toLocaleString('en-IN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </>
                )}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center', margin: '12px 0' }}>
              <button onClick={() => setEditFreeze(p => ({ ...p, newQty: Math.max(1, p.newQty - 1) }))} style={{ width: 40, height: 40, border: '2px solid #333', background: '#fff', borderRadius: '50%', fontSize: 20, fontWeight: 'bold', cursor: 'pointer' }}>−</button>
              <input
                type="number"
                min="1"
                value={editFreeze.newQty}
                onChange={(e) => {
                  const value = parseInt(e.target.value) || 1;
                  setEditFreeze(p => ({ ...p, newQty: Math.max(1, value) }));
                }}
                style={{
                  minWidth: 70,
                  height: 40,
                  textAlign: 'center',
                  fontSize: 18,
                  fontWeight: 600,
                  border: '2px solid #e9ecef',
                  borderRadius: 8,
                  outline: 'none',
                  padding: '0 8px'
                }}
                onFocus={(e) => e.target.select()}
              />
              <button onClick={() => setEditFreeze(p => ({ ...p, newQty: p.newQty + 1 }))} style={{ width: 40, height: 40, border: '2px solid #333', background: '#fff', borderRadius: '50%', fontSize: 20, fontWeight: 'bold', cursor: 'pointer' }}>+</button>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setEditFreeze({ isOpen: false, record: null, newQty: 1 })} style={{ flex: 1, padding: 10, background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer' }}>Cancel</button>
              <button onClick={async () => {
                try {
                  // Re-fetch latest user freezes to avoid stale ID (fixes 404 issues)
                  const latestRes = await api.get('/frozen-quantities/user');
                  let latestForProduct = null; let totalForProduct = 0;
                  (latestRes.data || []).forEach(fq => {
                    if (fq?.product?.id === editFreeze.latest?.product_id || fq?.product?.id === editFreeze.latest?.product?.id) {
                      totalForProduct += Number(fq.quantity || 0);
                      if (!latestForProduct || new Date(fq.frozen_at) > new Date(latestForProduct.frozen_at)) latestForProduct = fq;
                    }
                  });

                  const desired = editFreeze.newQty;
                  const delta = desired - (totalForProduct || editFreeze.total);
                  const base = latestForProduct?.quantity || editFreeze.latest.quantity || 0;
                  const targetId = latestForProduct?.id || editFreeze.latest.id;
                  const newLatestQty = Math.max(1, base + delta);

                  await api.put(`/frozen-quantities/update-quantity/${targetId}`, { quantity: newLatestQty });
                  showSuccess('Freeze quantity updated');
                  setEditFreeze({ isOpen: false, latest: null, total: 0, newQty: 1 });
                  // Refresh user frozen
                  const res = await api.get('/frozen-quantities/user');
                  const map = {};
                  (res.data || []).forEach(fq => {
                    if (!fq?.product?.id) return; const pid = fq.product.id;
                    if (!map[pid]) map[pid] = { total: 0, latest: fq, product: fq.product, records: [] };
                    map[pid].total += Number(fq.quantity || 0);
                    map[pid].records.push(fq);
                    if (new Date(fq.frozen_at) > new Date(map[pid].latest.frozen_at)) map[pid].latest = fq;
                  });
                  setUserFrozen(map);
                } catch (e) {
                  // Fallback approach: release all user freezes for product, then freeze desired total
                  try {
                    const latestRes2 = await api.get('/frozen-quantities/user');
                    const records = (latestRes2.data || []).filter(fq => fq?.product?.id === (editFreeze.latest?.product_id || editFreeze.latest?.product?.id));
                    const desired = editFreeze.newQty;
                    // Release all current
                    for (const fq of records) {
                      try { await api.put(`/frozen-quantities/release/${fq.id}`); } catch { }
                    }
                    // Re-freeze desired (if > 0)
                    if (desired > 0) {
                      await api.post('/frozen-quantities/freeze', { product_id: records[0]?.product?.id || editFreeze.latest?.product_id || editFreeze.latest?.product?.id, quantity: desired });
                    }
                    showSuccess('Freeze quantity updated');
                    setEditFreeze({ isOpen: false, latest: null, total: 0, newQty: 1 });
                    const res = await api.get('/frozen-quantities/user');
                    const map = {};
                    (res.data || []).forEach(fq => {
                      if (!fq?.product?.id) return; const pid = fq.product.id;
                      if (!map[pid]) map[pid] = { total: 0, latest: fq, product: fq.product, records: [] };
                      map[pid].total += Number(fq.quantity || 0);
                      map[pid].records.push(fq);
                      if (new Date(fq.frozen_at) > new Date(map[pid].latest.frozen_at)) map[pid].latest = fq;
                    });
                    setUserFrozen(map);
                  } catch (fallbackErr) {
                    showError(fallbackErr.response?.data?.message || 'Failed to update');
                  }
                }
              }} style={{ flex: 1, padding: 10, background: '#000', color: '#fff', border: '1px solid #000', borderRadius: 8, cursor: 'pointer' }}>Save</button>
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default ProductsPage;
